import { Handshake } from "lucide-react";

interface AnalysisMetadata {
  status?: string;
  confidence?: number;
  reason?: string;
  evidence?: string[];
  restriction_type?: string;
}

function parseMetadata(raw?: string | null): AnalysisMetadata | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

// Different job pipelines report confidence on different scales — LinkedIn's is
// mostly 0-1, TheirStack's is 0-100 — so normalize anything above 1 as already
// being a percentage rather than a fraction to multiply.
function formatConfidence(confidence: number): string {
  const pct = confidence <= 1 ? confidence * 100 : confidence;
  return `${Math.round(pct)}%`;
}

interface Props {
  companyCanApply?: boolean | null;
  metadataJson?: string | null;
  className?: string;
}

/** Eye-catching chip shown only when the LLM has clearly confirmed a job can be applied to directly with the company (vs. an agency-blocked posting). Hover reveals the underlying analysis. */
export default function CompanyApplyChip({ companyCanApply, metadataJson, className }: Props) {
  if (companyCanApply !== true) return null;

  const metadata = parseMetadata(metadataJson);

  return (
    <span className={`group relative inline-flex shrink-0 ${className ?? ""}`}>
      <span className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-inter font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 ring-2 ring-amber-400/50 shadow-[0_0_10px_rgba(245,158,11,0.6)] animate-pulse cursor-default">
        <Handshake className="w-3 h-3" />
        Direct to Company
      </span>

      {metadata && (
        // pt-2 (not mt-2) keeps the gap inside this element's own hoverable box, so moving
        // the mouse from the chip down into the popover never crosses a dead zone that would
        // drop group-hover and hide the popover before the cursor arrives.
        <div className="hidden group-hover:block absolute z-30 top-full right-0 w-64 pt-2">
          <div className="bg-surface-muted border border-border rounded-xl shadow-2xl p-3 text-left">
            <p className="text-[11px] font-semibold text-amber-400 font-inter mb-1.5">Company-Apply Analysis</p>
            {metadata.confidence !== undefined && (
              <p className="text-[11px] text-muted-foreground font-inter mb-1">
                Confidence: <span className="text-foreground font-medium">{formatConfidence(metadata.confidence)}</span>
              </p>
            )}
            {metadata.restriction_type && (
              <p className="text-[11px] text-muted-foreground font-inter mb-1">
                Restriction: <span className="text-foreground font-medium">{metadata.restriction_type}</span>
              </p>
            )}
            {metadata.reason && (
              <p className="text-[11px] text-muted-foreground font-inter leading-snug mb-1.5">{metadata.reason}</p>
            )}
            {metadata.evidence && metadata.evidence.length > 0 && (
              <ul className="list-disc list-inside space-y-0.5">
                {metadata.evidence.map((e, i) => (
                  <li key={i} className="text-[10px] text-muted-foreground font-inter">{e}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
